import React, { useEffect, useState } from 'react'
import { runInAction } from 'mobx'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { Button, Popconfirm, Popover, Select, Space, Tag } from 'antd'
import { InfoOutlined } from '@ant-design/icons'
import { v4 as uuidv4 } from 'uuid'
import { useStores } from '../../../cls/stores/MobXProvider'

import {
	Filter,
	UserString,
	LstTable,
	FormatedTimestamp,
	CustomTable
} from '../../../components/Shared'
import { Link, useNavigate } from 'react-router-dom'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import {
	actSaveRecruitment,
	newRecruitment,
	requireRecruitmentById
} from '../../../stores/recruitment/actions'
import {
	actLoadRecruitments,
	actUndoOrDeleteRecruitment
} from '../../../stores/ui-lst-recruitment/actions'
import dayjs from 'dayjs'
import { API_BASE_URL } from '../../../config'
import { translate as t } from '../../../cls/TranslationContext'
import { useSmartTranslation } from '../../../cls/TranslationContext'

export const recruitmentProgressOptions = [
	{
		key: 'by-progress',
		value: 'pending, reviewed, approved',
		label: t('__by%single.status')
	},
	{
		key: 'by-progress-pending',
		value: 'pending',
		label: t('single.pending')
	},
	{
		key: 'by-progress-reviewed',
		label: t('single.reviewed'),
		value: 'reviewed'
	},
	{
		key: 'by-progress-approved',
		value: 'approved',
		label: t('single.approved')
	},
	{
		key: 'by-progress-rejected',
		value: 'rejected',
		label: t('single.rejected')
	}
]

/**
 * @function LstItem
 * @description Renders a single row in the recruitment list. Handles conditional actions,
 * displays status tags, CV file links, and triggers delete/undo popups.
 *
 * @param {object} props
 * @param {string} props.id - The recruitment item's ID.
 * @param {boolean} props.isFirst - Flag to mark the first item for guided tours.
 */
const LstItem = observer(({ id, isFirst }) => {
	const navigate = useNavigate()
	const _itm = requireRecruitmentById(id)

	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] = useState(false)
	/**
	 * @function confirmUndoOrDelete
	 * @description Handles the confirmation logic for deleting or undoing a recruitment item.
	 * It checks whether the item is already marked for deletion and calls the appropriate action.
	 */
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)

		if (!!_itm.to_delete) {
			actUndoOrDeleteRecruitment(id, false).catch(console.error)
		} else {
			actUndoOrDeleteRecruitment(id, true).catch(console.error)
		}
	}

	const _actions = [
		{
			key: 'view',
			label: t('single.view'),
			value: 'view'
		},
		{
			key: 'data',
			label: t('__edit%'),
			value: 'data',
			disabled: !!_itm.to_delete
		}
	]

	if (_itm.to_delete) {
		_actions.push({
			key: 'undo-del',
			label: t('single.delete-undo'),
			value: 'undo-delete'
		})
	} else {
		_actions.push({
			key: 'del',
			label: t('single.delete'),
			value: 'delete'
		})
	}

	/**
	 * @function handleActions
	 * @description Executes navigation or triggers confirmation based on the selected action
	 * from the dropdown menu (view, edit, delete, undo-delete).
	 *
	 * @param {string} value - The selected action key.
	 */
	const handleActions = (value) => {
		switch (value) {
			case 'view':
				navigate(`${id}/view`)
				break
			case 'data':
				navigate(`${id}/edit`)
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			default:
				break
		}
	}

	return (
		<tr>
			<td>
				<Link to={`${id}/view`} id={isFirst ? 'target-itm-link' : ''}>
					{_itm.name}
				</Link>
			</td>
			<td>
				{
					_itm.cv.length > 0
						? _itm.cv
								.map((cv, index) => (
									<a
										key={index}
										id={isFirst ? 'target-itm-cv' : ''}
										href={API_BASE_URL + 'uploads/' + cv}
										target="_blank"
										rel="noreferrer"
									>
										{cv.split('.').pop().toUpperCase()}{' '}
										{_itm.cv.lenght > 1 && (
											<sup>({index + 1})</sup>
										)}
									</a>
								))
								.reduce((prev, curr) => [prev, ', ', curr])
						: t('single.no-cv') // TODO: translate
				}
			</td>

			<td id={isFirst ? 'target-itm-progress' : ''}>
				<Tag
					color={
						_itm.progress === 'pending'
							? 'warning'
							: _itm.progress === 'reviewed'
							? 'processing'
							: _itm.progress === 'approved'
							? 'success'
							: _itm.progress === 'rejected'
							? 'error'
							: 'default'
					}
				>
					{_itm.progress}
				</Tag>
			</td>
			<td>
				<Space
					direction="vertical"
					align="end"
					style={{ width: '100%' }}
					id={isFirst ? 'target-itm-actions' : ''}
				>
					<Space style={{ width: '100%' }} align="end">
						<Popover
							id={isFirst ? 'target-itm-info' : ''}
							content={() => (
								<CustomTable
									data={[
										[
											{
												item: (
													<strong>
														{t('single.info')}
													</strong>
												)
											}
										],
										[
											t('single.created-at'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															_itm.createdTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-by.label'),
											{
												item: _itm.createdBy ? (
													<UserString
														userId={_itm.createdBy}
														path="name"
													/>
												) : (
													<span>
														{t(
															'single.apply-job.action-from-form'
														)}
													</span>
												)
											}
										]
									]}
								/>
							)}
							trigger="hover"
						>
							<Button
								id={isFirst ? 'target-itm-info' : ''}
								color="default"
								icon={<InfoOutlined />}
							/>
						</Popover>

						<Popconfirm
							title={
								_itm.to_delete
									? t('single.confirm-undo-delete')
									: t('single.confirm-delete')
							}
							open={showConfirmUndoOrDelete}
							onConfirm={confirmUndoOrDelete}
							onCancel={() => setConfirmUndoOrDelete(false)}
							okType="link"
							placement="topLeft"
							getPopupContainer={(triggerNode) =>
								triggerNode.parentNode
							}
						>
							<Select
								id={isFirst ? 'target-itm-actions' : ''}
								value={t('single.actions')}
								options={_actions}
								onChange={handleActions}
								style={{ width: 120 }}
							/>
						</Popconfirm>
					</Space>
				</Space>

				{_itm.to_delete && (
					<div>
						<span>
							{t('single.rejected-on')}
							<FormatedTimestamp timestamp={_itm.to_delete} />
						</span>
					</div>
				)}
			</td>
		</tr>
	)
})
LstItem.propTypes = {
	id: PropTypes.string.isRequired
}

/**
 * @function RecruitmentLst
 * @description Renders the recruitment list component. Handles filtering, loading data,
 * tour setup, and supports a button to add new recruitment entries.
 */
const RecruitmentLst = observer(() => {
	const navigate = useNavigate()

	const { uiLstRecruitment, appType, users, tourStore } = useStores()
	const { loggedInUserId } = users

	const bySearch = uiLstRecruitment.search
	const byProgress = uiLstRecruitment.filter.progress
	const byShowAll = uiLstRecruitment.filter.show_all
	const { t } = useSmartTranslation()

	// Load notifications on filter changes
	useEffect(() => {
		actLoadRecruitments()
	}, [bySearch, byProgress, byShowAll, appType.appIs])

	const recruitmentLst = getTourArrayByKeys('recruitmentLst', ['all'])
	const tourFilter = [
		...getTourArrayByKeys('filter', [
			'section',
			'search',
			'progress',
			'new'
		])
	]

	/**
	 * @function startTour
	 * @description Initiates a guided UI tour for the recruitment list.
	 * If the list is empty, it creates a demo recruitment item to populate the table.
	 */
	const startTour = () => {
		const hasItems = uiLstRecruitment.ids.length > 0
		if (!hasItems) {
			const newId = uuidv4()
			tourStore.setDeleteItemAct(() => {
				runInAction(() => {
					uiLstRecruitment.setIds(
						uiLstRecruitment.ids.filter((id) => id !== newId)
					)
					uiLstRecruitment.setNr(uiLstRecruitment.nr - 1)
				})
			})

			const demoObj = {
				recruitment_id: newId,
				name: 'John Doe',
				cv: [],
				progress: 'active',
				createdBy: loggedInUserId,
				createdTs: dayjs().unix()
			}
			actSaveRecruitment(newId, demoObj, true).catch(console.error)
		}
		tourStore.startTour('recruitmentLst-tour', [
			...tourFilter,
			...recruitmentLst
		])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_recruitment') }}
				buttonProps={{
					onClick: startTour
				}}
			/>
			<Filter
				uiStore={uiLstRecruitment}
				isForNotification={true}
				addNew={() => {
					const newId = uuidv4()
					newRecruitment(newId).then(() => {
						navigate(`/recruitments/${newId}/edit`)
					})
				}}
				hide={[
					'priority',
					'category',
					'by-user',
					'for-user',
					'orderBy',
					'deleted',
					'entity_table',
					'kind',
					'show_all'
				]}
				progressOptions={recruitmentProgressOptions}
			/>
			<LstTable
				LstItem={LstItem}
				store={uiLstRecruitment}
				className="table-pro"
			>
				<tr id="items-lst">
					<th id="target-recruitment">{t('single.name')}</th>
					<th id="target-cv">CV</th>
					<th id="target-progress">{t('single.status')}</th>
					<th id="target-actions">{t('single.actions')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})
export default RecruitmentLst
